import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    const formData = new FormData(form);
    const formUrl = form.getAttribute('action');
    if (!formUrl) {
      setFormStatus('error');
      setFormError('表单配置错误，请稍后重试');
      return;
    }

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的网络连接');
    }
  };

  return (
    <>
      <SEOSchema type="about" />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mb-14">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                关于韩漫屋
              </h1>
              <div className="bg-background-50 rounded-2xl p-6 md:p-8">
                <div className="prose max-w-none">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    韩漫屋成立于2024年，是一个专注于韩国漫画在线阅读的平台。我们致力于为中文读者提供最优质的韩漫阅读体验——韩漫免费看，下拉式漫画大全，韩国漫画在线。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    在韩漫屋享受韩漫免费看的乐趣，下拉式漫画大全与友好的韩国漫画在线界面，为您营造温馨舒适的阅读环境。我们的团队由一群热爱韩国漫画的年轻人组成，深知漫迷们对优质阅读体验的渴望。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    韩漫屋采用先进的下拉式阅读技术，让用户可以在任何设备上流畅阅读韩国漫画。无论是手机、平板还是电脑，都能获得舒适的阅读体验。我们收录了涵盖恋爱、热血、奇幻、悬疑、搞笑、剧情、BL、GL等多种类型的韩国漫画作品。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们的愿景是成为中文世界最受欢迎的韩国漫画平台，让每一位漫迷都能在这里找到自己喜欢的作品。韩漫屋始终坚持免费阅读的原则，通过合法版权合作与广告收入维持运营，让优质内容惠及更多读者。
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-8 pt-8 border-t border-background-200">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>100+</p>
                    <p className="text-xs text-foreground-500 mt-1">漫画作品</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>50万+</p>
                    <p className="text-xs text-foreground-500 mt-1">月活用户</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary-600" style={{ fontFamily: 'var(--font-heading)' }}>99.9%</p>
                    <p className="text-xs text-foreground-500 mt-1">服务可用率</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                读者口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {testimonials.map((item, index) => (
                  <div key={index} className="bg-background-50 rounded-xl p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(item.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-400 text-sm" />
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      {item.content}
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-sm font-bold">
                        {item.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-950">{item.name}</p>
                        <p className="text-xs text-foreground-400">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">电子邮件</p>
                      <p className="text-sm text-foreground-500">contact@neoom.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-wechat-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">微信公众号</p>
                      <p className="text-sm text-foreground-500">韩漫屋</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className="ri-map-pin-line text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">办公地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区望京街道88号</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-foreground-950 mb-4">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 bg-emerald-50 text-emerald-700 text-sm rounded-lg">
                      消息发送成功！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                      {formError}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} action="https://readdy.ai/api/form/d9peukpn2trhb35gve70" method="POST" className="space-y-4" data-readdy-form="">
                    <div>
                      <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={3}
                        className="w-full px-3 py-2.5 bg-background-100 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                    </div>
                    <input
                      name="website_alt"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute opacity-0 pointer-events-none"
                      style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
                    />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}